"use client";

import { useMemo, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "../shadcn/button";
import { cn } from "@/lib/utils";
import ImagesCarrouselDialog from "../Dialogs/ImagesCarrouselDialog";
import { getSpotFileUrl } from "@/utils/generic";
import { useChatDisplayOptionsContext } from "./useChatDisplayOptions";
import type { NoxusDataTypes } from "./noxus-message-types";
import type { MessagePart } from "./utils";

type FileAttachment = NoxusDataTypes["file_attachment"];

export function isSourceImagePart(part: MessagePart): boolean {
  if (part.type !== "data-file_attachment" || !("data" in part)) return false;
  const data = part.data as FileAttachment;
  return !!data?.source_image && !!data?.content_type?.startsWith("image/");
}

type SourceImageAttachmentsProps = {
  parts: MessagePart[];
  isPublic?: boolean;
};

export function SourceImageAttachments({
  parts,
  isPublic = false,
}: SourceImageAttachmentsProps) {
  const { showSourceImagesByDefault } = useChatDisplayOptionsContext();
  const [visible, setVisible] = useState(showSourceImagesByDefault);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  const images = useMemo(() => {
    const seenFileIds = new Set<string>();
    const urls: string[] = [];
    for (const part of parts) {
      if (!isSourceImagePart(part) || !("data" in part)) continue;
      const data = part.data as FileAttachment;
      if (seenFileIds.has(data.file_id)) continue;
      seenFileIds.add(data.file_id);
      urls.push(
        data.url.startsWith("spot://")
          ? getSpotFileUrl(data.url, true, isPublic)
          : data.url
      );
    }
    return urls;
  }, [parts, isPublic]);

  if (images.length === 0) return null;

  const displayImages = images.slice(0, 3);
  const remainingCount = images.length - 3;
  const showOverlay = remainingCount > 0;

  const handleImageClick = (index: number) => {
    setStartIndex(index);
    setCarouselOpen(true);
  };

  return (
    <div className="mt-3">
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1 px-2.5 text-xs text-muted-foreground"
        onClick={() => setVisible((v) => !v)}
      >
        {visible ? (
          <EyeOff className="size-3.5" />
        ) : (
          <Eye className="size-3.5" />
        )}
        {visible
          ? "Hide source images"
          : `Show source images (${images.length})`}
      </Button>
      {visible && (
        <div className="flex gap-2 mt-2">
          {displayImages.map((image, index) => {
            const isLastVisible = index === 2 && showOverlay;

            return (
              <button
                key={image}
                onClick={() => handleImageClick(index)}
                className="relative size-[60px] rounded-md overflow-hidden border border-border cursor-pointer hover:opacity-80 transition-opacity"
              >
                <img
                  src={image}
                  alt=""
                  className={cn(
                    "size-full object-cover",
                    isLastVisible && "blur-sm"
                  )}
                />
                {isLastVisible && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <span className="text-white font-semibold text-sm">
                      +{remainingCount}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
      <ImagesCarrouselDialog
        images={images}
        isOpen={carouselOpen}
        onOpenChange={setCarouselOpen}
        startIndex={startIndex}
      />
    </div>
  );
}
